import { useEffect, useMemo, useState } from 'react';

import {
  BULK_PDF_EXPORT_LIMIT,
  GetScheduleFormsRequestDTO,
  GetScheduleFormsResponseDTO,
  MODULE,
  ScheduleFormOrderByFieldEnumSchema,
  ScheduleFormSubmissionColumnId,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import DownloadPdfIcon from '@/components/icons/download-pdf';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { buildFilterFields } from '@/queries/column-filter';
import { getScheduleFormsInfiniteQueryOptions, scheduleFormKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { exportRecordsToPdfZip } from '@/utils/bulk-pdf-export';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { buildPdfExportJob } from '@/utils/register-pdf-export';

import {
  AssetScheduleRecordsTable,
  NoScheduleFormSubmissionAvailableDisplay,
  NoScheduleFormSubmissionFoundFromFiltersDisplay,
  NoScheduleFormSubmissionFoundFromSearchDisplay,
} from './table';
import { FILTER_FIELD_MAP, groupOptions, columns as scheduleFormColumns } from './table-meta';

const AssetScheduleRecordsPage = () => {
  const { id } = useParams();

  const queryClient = useQueryClient();
  const toast = useToast();
  const { setModuleFilters } = useModulesStore();
  const { canEditScheduleRecord, canExportCsv } = usePermissions();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.AssetScheduleRecords, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns: scheduleFormColumns(),
  });

  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const [isDownloadingPdfs, setIsDownloadingPdfs] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: scheduleFormsData,
    fetchNextPage: fetchNextScheduleFormsPage,
    hasNextPage: hasNextScheduleFormsPage,
    isFetching: isFetchingScheduleForms,
    isFetchingNextPage: isFetchingNextScheduleFormsPage,
    error: scheduleFormsError,
  } = useInfiniteQuery(
    getScheduleFormsInfiniteQueryOptions({
      filters: {
        search: debouncedSearch,
        ...buildFilterFields(filters, FILTER_FIELD_MAP, {
          transformValue: (columnId, raw) => (columnId === 'formNumber' ? (raw as string | undefined)?.replace(/[^0-9]/g, '') : raw),
          extraFields: { assetId: [id as string] },
        }),
      },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetScheduleFormsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as z.infer<typeof ScheduleFormOrderByFieldEnumSchema>, direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextScheduleFormsPage,
    hasNextPage: hasNextScheduleFormsPage,
    isFetching: isFetchingScheduleForms,
    isError: !!scheduleFormsError,
    root: scrollRoot,
  });

  const getSelectedScheduleForms = useMemo(() => {
    return (incomingData: typeof scheduleFormsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutate: deleteScheduleForms, isPending: isDeletingScheduleForms } = useMutation({
    mutationFn: async (ids: string[]) => await ApiService.DeleteScheduleForms({ scheduleFormIds: ids }),
    onSuccess: () => {
      toast.success('Schedule forms deleted successfully');
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleDeleteScheduleForms = () => {
    if (scheduleFormsData?.pages) {
      const toDelete = getSelectedScheduleForms(scheduleFormsData);
      deleteScheduleForms(toDelete.map((scheduleForm) => scheduleForm.id));
    }
  };

  const selectedScheduleForms = useMemo(() => getSelectedScheduleForms(scheduleFormsData), [scheduleFormsData, getSelectedScheduleForms]);
  const canBulkActOnSelectedScheduleForms =
    selectedScheduleForms.length > 0 && selectedScheduleForms.every((scheduleForm) => canEditScheduleRecord(scheduleForm.submittedById));

  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  function handleCsvExport() {
    if (!scheduleFormsData?.pages) return;

    const selected = getSelectedScheduleForms(scheduleFormsData);

    const activeColumnIds = columnOrder.filter((column) => column.id !== 'select' && column.active).map((column) => column.id);
    openExportTab({
      kind: 'csv',
      request: {
        queryType: 'schedule-forms',
        filename: buildCsvExportFilename('schedule-forms'),
        filters: { fields: { id: selected.map((scheduleForm) => scheduleForm.id) } },
        groupBy: grouping.length
          ? { field: grouping[0] as NonNullable<GetScheduleFormsRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
          : undefined,
        orderBy: sorting.key
          ? { field: sorting.key as z.infer<typeof ScheduleFormOrderByFieldEnumSchema>, direction: sorting.direction as 'asc' | 'desc' }
          : undefined,
        columns: activeColumnIds,
      },
    });
  }

  async function handlePdfExport() {
    if (!scheduleFormsData?.pages) return;
    const selected = getSelectedScheduleForms(scheduleFormsData);
    if (isRegisterPdfExportEnabled) {
      if (selected.length > BULK_PDF_EXPORT_LIMIT) {
        toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
        return;
      }
      openExportTab(buildPdfExportJob({ scope: 'records', recordType: 'schedule-form', records: selected }));
      return;
    }
    setIsDownloadingPdfs(true);
    try {
      await exportRecordsToPdfZip(selected, 'schedule-forms');
      toast.success('PDF export completed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export PDFs');
    } finally {
      setIsDownloadingPdfs(false);
    }
  }

  useEffect(() => {
    if (scheduleFormsError) {
      toast.error(scheduleFormsError.message);
    }
  }, [toast, scheduleFormsError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const flattenedData = useMemo(() => scheduleFormsData?.pages.flatMap((page) => page.data) ?? [], [scheduleFormsData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedScheduleForms.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.AssetScheduleRecords, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<GetScheduleFormsResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      onOpenChange={setOpenColumnsDialog}
                      columns={scheduleFormColumns()}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: ScheduleFormSubmissionColumnId; active: boolean }> },
                        })
                      }
                    />
                  </div>
                </div>
              </>
            )}

            {selectedScheduleForms.length > 0 && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedScheduleForms.length} totalCount={scheduleFormsData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                <TablePageHeaderActionButton icon={<DownloadPdfIcon />} label="Download PDF" onClick={handlePdfExport} disabled={isDownloadingPdfs} />
                {canBulkActOnSelectedScheduleForms && (
                  <TablePageHeaderActionButton
                    icon={<DeleteIcon className="text-default-red" />}
                    label="Delete"
                    onClick={() => setOpenDeleteConfirmation(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <AssetScheduleRecordsTable
          data={flattenedData}
          loading={(isFetchingScheduleForms && !isFetchingNextScheduleFormsPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoScheduleFormSubmissionFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoScheduleFormSubmissionFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.AssetScheduleRecords, [])} />
            ) : (
              <NoScheduleFormSubmissionAvailableDisplay />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters: ColumnFiltersState) => setModuleFilters(MODULE.AssetScheduleRecords, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextScheduleFormsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextScheduleFormsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteScheduleForms}
        loading={isDeletingScheduleForms}
        title="Delete schedule form records?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these schedule form records?"
      />
    </div>
  );
};

export default AssetScheduleRecordsPage;
